import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Banknote,
  FileText,
  History,
  Info,
  Truck,
  User,
  UserCheck,
} from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { adminApi, fileUrl } from '../../api/client';
import AssignTranslatorForm from '../../components/dashboard/AssignTranslatorForm';
import DashModal from '../../components/dashboard/DashModal';
import StatusBadge from '../../components/dashboard/StatusBadge';
import './AdminOrders.css';

const HISTORY_LABELS = {
  created: 'Commande créée',
  payment_received: 'Paiement reçu',
  payment_failed: 'Paiement échoué',
  matching_proposal: 'Matching automatique',
  assigned: 'Professionnel affecté',
  unassigned: 'Professionnel désaffecté',
  status_changed: 'Statut modifié',
  mission_assigned: 'Mission envoyée',
  mission_accepted: 'Mission acceptée',
  mission_refused: 'Mission refusée',
  mission_in_progress: 'Traduction commencée',
  mission_ready: 'Document déposé',
  mission_delivered: 'Document livré',
  mission_cancelled: 'Mission annulée',
  document_uploaded: 'Document déposé',
  document_added: 'Document ajouté',
  delivery_updated: 'Livraison mise à jour',
  verifier_assigned: 'Vérificateur affecté',
};

const DELIVERY_MODE_LABELS = {
  online: 'En ligne',
  email: 'Email',
  pickup: 'Retrait sur place',
  post: 'La Poste',
  express: 'Livraison rapide',
};

const DOC_KIND_LABELS = { source: 'Source', draft: 'Brouillon', final: 'Final' };

const PRIORITY_LABELS = { normal: 'Normal', high: 'Rapide', urgent: 'Urgent' };

const CLOSED_STATUSES = ['delivered', 'completed', 'cancelled', 'refused', 'payment_failed', 'quote_expired'];

function deliveryStatus(order) {
  const di = order.deliveryInfo || {};
  if (di.deliveredAt) return 'Livrée';
  if (di.sentAt) return 'Envoyée';
  return 'En attente';
}

function Row({ label, children }) {
  return (
    <div className="od-row">
      <span className="od-label">{label}</span>
      <span className="od-value">{children}</span>
    </div>
  );
}

export default function AdminOrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const d = t.dashboard;
  const a = d.admin;
  const [order, setOrder] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editForm, setEditForm] = useState({ price: '', deadline: '', adminNotes: '' });
  const [statusForm, setStatusForm] = useState('');
  const [msg, setMsg] = useState('');

  const reload = async () => {
    const res = await adminApi.order(id);
    const o = res.order;
    setOrder(o);
    setEditForm({
      price: o?.price ?? '',
      deadline: o?.deadline || '',
      adminNotes: o?.adminNotes || '',
    });
    setStatusForm(o?.status || '');
  };

  useEffect(() => {
    reload().catch(() => setOrder(null));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!order) return <p className="dash-empty">{d.empty}</p>;

  const details = order.details || {};
  const contact = order.contact || {};
  const breakdown = details.pricingBreakdown || null;
  const deliveryFee = order.deliveryInfo?.fee;
  const isInterpreting = order.category === 'interpreting';
  const clientName = [contact.civility, contact.firstName, contact.lastName].filter(Boolean).join(' ') || order.client || '—';
  const closed = CLOSED_STATUSES.includes(order.status);

  const saveEdit = async () => {
    setMsg('');
    try {
      await adminApi.updateOrderMeta(id, {
        price: editForm.price,
        deadline: editForm.deadline || null,
        adminNotes: editForm.adminNotes,
      });
      await reload();
      setEditOpen(false);
      setMsg('Commande mise à jour');
    } catch {
      setMsg('Error');
    }
  };

  const saveStatus = async () => {
    setMsg('');
    try {
      await adminApi.updateOrderMeta(id, { status: statusForm });
      await reload();
      setStatusOpen(false);
      setMsg('Statut mis à jour');
    } catch {
      setMsg('Error');
    }
  };

  const unassign = async () => {
    setMsg('');
    try {
      await adminApi.unassignOrder(id);
      await reload();
    } catch {
      setMsg('Error');
    }
  };

  return (
    <>
      <header className="dash-page-head">
        <button type="button" className="dash-link" onClick={() => navigate('/admin/orders')}>{a.ordersTitle}</button>
        <h1>
          Commande {order.id}{' '}
          <StatusBadge status={order.status} labels={d.status} />
        </h1>
        <p>
          {order.document} · <strong>{order.sourceLang} → {order.targetLang}</strong>
          {' · '}Créée le {order.date || '—'}
        </p>
        <div className="dash-actions" style={{ marginTop: 16, marginBottom: 0 }}>
          {!closed && (
            <button type="button" className="btn btn-secondary" onClick={() => { setEditOpen(true); setStatusOpen(false); }}>
              Modifier
            </button>
          )}
          {!closed && (
            <button type="button" className="btn btn-primary" onClick={() => setAssignOpen(true)}>
              {order.translator ? 'Changer le professionnel' : 'Assigner un professionnel'}
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => { setStatusOpen(true); setEditOpen(false); }}>
            Changer statut
          </button>
        </div>
      </header>

      <DashModal open={editOpen} title={`Modifier — ${order.id}`} onClose={() => setEditOpen(false)}>
        <div className="dash-form">
          <div className="dash-field">
            <label>{a.colTotal} (TND)</label>
            <input type="number" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} />
          </div>
          <div className="dash-field">
            <label>Date limite</label>
            <input type="date" value={editForm.deadline} onChange={(e) => setEditForm({ ...editForm, deadline: e.target.value })} />
          </div>
          <div className="dash-field">
            <label>{a.adminNotes}</label>
            <textarea rows={3} value={editForm.adminNotes} onChange={(e) => setEditForm({ ...editForm, adminNotes: e.target.value })} />
          </div>
          <button type="button" className="btn btn-primary" onClick={saveEdit}>{d.save}</button>
        </div>
      </DashModal>

      <DashModal open={statusOpen} title={`Changer statut — ${order.id}`} onClose={() => setStatusOpen(false)}>
        <div className="dash-form">
          <div className="dash-field">
            <label>Statut</label>
            <select value={statusForm} onChange={(e) => setStatusForm(e.target.value)}>
              {['draft','request_sent','pending_review','quote_sent','quote_accepted','payment_pending','paid','translator_to_assign','translator_assigned','translation_in_progress','translation_submitted','verification_requested','lawyer_notary_in_progress','correction_requested','new_version','verification_validated','final_control','ready_to_deliver','delivered','completed','refused','cancelled','payment_failed','quote_expired','translator_unavailable','pending','assigned','in_progress','ready'].map((s) => (
                <option key={s} value={s}>{d.status[s] || s}</option>
              ))}
            </select>
          </div>
          <button type="button" className="btn btn-primary" onClick={saveStatus}>{d.save}</button>
        </div>
      </DashModal>

      <div className="od-grid">
        <div>
          <section className="dash-panel">
            <h2><Info size={16} /> Informations de la commande</h2>
            <dl className="od-dl">
              <Row label="Prestation">{isInterpreting ? 'Interprétariat' : 'Traduction'}</Row>
              <Row label="Service">{details.translationService || details.interpretingType || '—'}</Row>
              <Row label="Langue source">{order.sourceLang}</Row>
              <Row label="Langue cible">{order.targetLang}</Row>
              {!isInterpreting && (
                <Row label="Nombre de pages">{order.pages}{details.words ? ` · ${details.words} mots` : ''}</Row>
              )}
              {isInterpreting && details.missionType ? <Row label="Type de mission">{details.missionType}</Row> : null}
              {isInterpreting && details.missionDate ? <Row label="Date de mission">{details.missionDate}</Row> : null}
              <Row label="Priorité">{d.priority?.[order.priority] || PRIORITY_LABELS[order.priority] || order.priority}</Row>
              <Row label="Date limite">{order.deadline || '—'}</Row>
              {order.notes ? <Row label="Instructions">{order.notes}</Row> : null}
            </dl>
          </section>

          <section className="dash-panel">
            <h2><FileText size={16} /> Documents</h2>
            {order.documents?.length > 0 ? (
              order.documents.map((doc) => (
                <div className="od-doc" key={doc.id || doc.name}>
                  <span className="od-doc-icon"><FileText size={18} /></span>
                  <span className="od-doc-body">
                    <strong>{doc.name}</strong>
                    <small>{DOC_KIND_LABELS[doc.kind] || doc.kind}</small>
                  </span>
                  <span className="od-doc-actions">
                    <a className="dash-link" href={fileUrl(doc.url)} target="_blank" rel="noreferrer">Visualiser</a>
                    <a className="dash-link" href={fileUrl(doc.url)} target="_blank" rel="noreferrer" download>Télécharger</a>
                  </span>
                </div>
              ))
            ) : <p className="dash-empty">Aucun document.</p>}
          </section>

          <section className="dash-panel">
            <h2><Banknote size={16} /> Prix</h2>
            <ul className="dash-file-list od-price">
              {breakdown ? (
                Object.entries(breakdown).filter(([, v]) => Number(v)).map(([k, v]) => (
                  <li key={k}><span style={{ textTransform: 'capitalize' }}>{k}</span><strong>{v} {order.currency || 'TND'}</strong></li>
                ))
              ) : order.price != null ? (
                <li><span>Prestation</span><strong>{order.price} {order.currency || 'TND'}</strong></li>
              ) : (
                <li><span>Prestation</span><strong>Tarif à confirmer</strong></li>
              )}
              {Number(deliveryFee) > 0 && (
                <li><span>Livraison</span><strong>{deliveryFee} {order.currency || 'TND'}</strong></li>
              )}
              <li className="od-total"><span>Total</span><strong>{order.price != null ? `${order.price} ${order.currency || 'TND'}` : '—'}</strong></li>
              <li>
                <span>Paiement</span>
                <StatusBadge status={order.paymentStatus || 'unpaid'} labels={d.payment} />
              </li>
            </ul>
          </section>
        </div>

        <div>
          <section className="dash-panel">
            <h2><User size={16} /> Client</h2>
            <div className="od-pro">
              <span className="od-avatar">{(clientName || '?').charAt(0).toUpperCase()}</span>
              <div className="od-pro-body">
                <strong>{clientName}</strong>
                <small>{contact.email || order.clientEmail || '—'}</small>
                <small>{contact.phone || '—'}</small>
              </div>
            </div>
            {order.clientId && (
              <div className="dash-actions" style={{ marginTop: 12, marginBottom: 0 }}>
                <Link className="btn btn-outline" to={`/admin/clients/${order.clientId}`}>
                  Voir le profil client
                </Link>
              </div>
            )}
          </section>

          <section className="dash-panel">
            <h2><UserCheck size={16} /> Professionnel affecté</h2>
            {order.translator ? (
              <>
                <div className="od-pro">
                  <span className="od-avatar">{(order.translator || '?').charAt(0).toUpperCase()}</span>
                  <div className="od-pro-body">
                    <strong>{order.translator}</strong>
                    <small>{order.sourceLang} → {order.targetLang}{details.translationService ? ` · ${details.translationService}` : ''}</small>
                    <small><span className="avail-pill avail-pill--available">Actif</span></small>
                  </div>
                </div>
                <div className="od-meta">
                  <div>
                    <em>Affectation</em>
                    <strong>{order.assignmentMethod === 'auto' ? 'Automatique' : 'Manuelle'}</strong>
                  </div>
                  <div>
                    <em>Date</em>
                    <strong>{order.assignedAt || '—'}</strong>
                  </div>
                </div>
                <div className="dash-actions" style={{ marginTop: 12, marginBottom: 0 }}>
                  {order.translatorId && (
                    <Link className="btn btn-outline" to={`/admin/translators/${order.translatorId}`}>
                      Voir profil
                    </Link>
                  )}
                  {!closed && (
                    <>
                      <button type="button" className="btn btn-outline" onClick={() => setAssignOpen(true)}>
                        Changer
                      </button>
                      <button type="button" className="btn btn-outline" onClick={unassign}>
                        Désaffecter
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <p className="assign-warn">⚠️ Aucun professionnel affecté</p>
            )}
          </section>

          <section className="dash-panel">
            <h2><Truck size={16} /> Livraison</h2>
            <dl className="od-dl">
              <Row label="Mode">{DELIVERY_MODE_LABELS[order.delivery] || order.delivery || '—'}</Row>
              <Row label="Email">{contact.email || order.clientEmail || '—'}</Row>
              {order.deliveryInfo?.address ? <Row label="Adresse">{order.deliveryInfo.address}</Row> : null}
              {order.deliveryInfo?.trackingNumber ? <Row label="Suivi">{order.deliveryInfo.trackingNumber}</Row> : null}
              <Row label="Statut">{deliveryStatus(order)}</Row>
            </dl>
          </section>

          <section className="dash-panel">
            <h2><History size={16} /> Historique</h2>
            {(order.history || []).length > 0 ? (
              <ol className="od-timeline">
                {[...order.history].reverse().map((h, i) => (
                  <li key={`${h.at}-${i}`}>
                    <span className="ot-time">
                      {h.at ? new Date(h.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </span>
                    <span className="ot-label">
                      {HISTORY_LABELS[h.action] || h.action}
                      {h.detail ? ` — ${h.detail}` : ''}
                    </span>
                    <span className="ot-by">par {h.by}</span>
                  </li>
                ))}
              </ol>
            ) : <p className="dash-empty">Aucun événement.</p>}
          </section>
        </div>
      </div>

      <AssignTranslatorForm
        order={order}
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onDone={reload}
      />

      {msg && <p className="dash-toast">{msg}</p>}
    </>
  );
}
